import * as React from 'react';
import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import { Notification } from '@coursera/coursera-ui';

import DataReportModal from 'bundles/account-settings/components/sections/data-report/DataReportModal';
import DataReleaseMutation from 'bundles/account-settings/components/sections/data-report/api/DataReleaseMutation.graphql';
import DataReportQuery from 'bundles/account-settings/components/sections/data-report/api/DataReportQuery.graphql';
import type {
  DataReleaseMutation as DataReleaseMutationData,
  DataReleaseMutationVariables,
} from 'bundles/account-settings/components/sections/data-report/api/__generated__/DataReleaseMutation';
import type {
  DataReportQuery as DataReportQueryData,
  DataReportQueryVariables,
} from 'bundles/account-settings/components/sections/data-report/api/__generated__/DataReportQuery';
import { useUserForcedSSOContext } from 'bundles/account-settings/contexts/UserForcedSSOContext';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/DataReport';

const DataReport = () => {
  const { id: userId } = getUser();
  const { isUserSsoLoginForced } = useUserForcedSSOContext();
  const { data } = useQuery<DataReportQueryData, DataReportQueryVariables>(DataReportQuery, {
    skip: !isAuthenticatedUser(),
    variables: {
      userId,
      includeInvitations: false,
      first: 100,
    },
    errorPolicy: 'all',
    context: {
      clientName: 'gatewayGql',
    },
  });

  const isEnterpriseLearner =
    !!data?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.totalCount;

  const [showConfirmEmailModal, setShowConfirmEmailModal] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [showRateLimitNotification, setShowRateLimitNotification] = useState(false);

  const handleOpenModal = () => {
    setShowConfirmEmailModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmEmailModal(false);
  };

  const [releaseData] = useMutation<DataReleaseMutationData, DataReleaseMutationVariables>(DataReleaseMutation, {
    onCompleted: () => {
      handleCloseModal();
      setShowSuccessNotification(true);
      setShowErrorNotification(false);
      setShowRateLimitNotification(false);
      setDisabled(false);
    },
    onError: (error) => {
      if (error.message.includes('throttled')) {
        setShowSuccessNotification(false);
        setShowErrorNotification(false);
        setShowRateLimitNotification(true);
        setDisabled(true);
      } else {
        setShowSuccessNotification(false);
        setShowErrorNotification(true);
        setShowRateLimitNotification(false);
        setDisabled(true);
      }
    },
    variables: { userId },
    context: { clientName: 'gatewayGql' },
  });

  const handleDataRequest = () => {
    setDisabled(true);

    releaseData();
  };

  const dismissNotification = () => {
    setShowSuccessNotification(false);
    setShowErrorNotification(false);
    setShowRateLimitNotification(false);
    setDisabled(false);
  };

  let notificationMessage;
  let notificationType;

  if (showSuccessNotification) {
    notificationMessage = _t('We are processing your request. You should receive an email soon.');
    notificationType = 'info';
  } else if (showErrorNotification) {
    notificationMessage = _t('The request was not successful. Please try again.');
    notificationType = 'error';
  } else if (showRateLimitNotification) {
    notificationMessage = _t(
      'It looks like you already made a request today. Please wait at least 24 hours between requests for your learner data report.'
    );
    notificationType = 'error';
  }

  return (
    <div className="rc-DataReport">
      {showConfirmEmailModal && (
        <DataReportModal
          handleCloseModal={handleCloseModal}
          handleDataRequest={handleDataRequest}
          isEnterpriseLearner={isEnterpriseLearner}
          disabled={disabled}
          showModal
          notificationMessage={notificationMessage}
          notificationType={showErrorNotification || showRateLimitNotification ? 'error' : undefined}
          onDismissNotification={dismissNotification}
        />
      )}
      {showSuccessNotification && (
        <div className="notification-banner">
          <Notification
            message={notificationMessage}
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            type={notificationType}
            onDismiss={dismissNotification}
            isTransient
            isDismissible
          />
        </div>
      )}
      <h2 className="display-2-text section-title">{_t('Learner Data Report')}</h2>
      <div>
        <p>
          {_t(
            'Request a report of all learner data stored by Coursera about your account. This report will be sent to the e-mail address below.'
          )}
        </p>
      </div>
      <div className="horizontal-box split-column">
        <div className="email-box">
          <p className="email-field">{getUser().email_address}</p>
        </div>
        <div className="send-report">
          <Button variant="secondary" onClick={handleOpenModal}>
            {_t('Send Report')}
          </Button>
        </div>
      </div>
      {!isUserSsoLoginForced && (
        <div className="horizontal-box">
          <div className="flex-1">
            <p className="adjust-email-text">
              {_t('To change email, please adjust the email settings at the top of this page.')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataReport;
